import { useState, useMemo, useRef, useEffect } from 'react';
import { useUser } from '../../../../../utils/UserContext';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { Customer, Truck } from '../../../../../types/dbTypes';
import {
  TransactionWithPayments,
  BudgetCategory,
  transactionPausedCategories,
  transactionDepositCategories,
  TruckAttributionInput,
} from '../../../../../types/apiTypes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import TransactionTable from './transactions/TransactionsTable';
import TransactionControls from './transactions/TransactionsControls';
import FilterModal from './transactions/FilterModal';
import {
  isPreActiveCustomer,
  getDemoTransactions,
  getDemoTrucks,
} from '../home/utils/demoData';
import { DemoDataAlert } from '../home/components/DemoDataAlert';
import {
  useTransactionsQuery,
  useTransactionsCountQuery,
  useTransactionMutations,
} from './hooks/useTransactions';
import useApi, {
  NotAuthenticatedError,
} from '../../../../../utils/AxiosWrapper';
import { ensureApiSuccess } from '../../../../../utils/unwrapApiResponse';
import { reportApiError } from '../../../../../utils/reportApiError';
import dayjs from 'dayjs';
import AttachInvoiceModal from './transactions/widgets/AttachInvoiceModal';
import SplitAllocationModal from '../../../../shared/SplitAllocationModal';
import { ExportModal } from './transactions/ExportModal';
import PendingTransactionsBand from './widgets/PendingTransactionsBand';

interface TransactionsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  budgetCategories: BudgetCategory[];
  trucks: Truck[];
  loadingTrucks: boolean;
}

function Transactions({
  budgetCategories,
  trucks,
  loadingTrucks,
}: TransactionsProps) {
  const { screenView } = useScreenView();
  const { userId, userRecord } = useUser();
  const navigate = useNavigate();
  const { callProtectedApi } = useApi();

  // Filters State
  const [filters, setFilters] = useState({
    offset: 0,
    limit: 20,
    searchQuery: '',
    startDate: '',
    endDate: dayjs().format('YYYY-MM-DD'),
    isDateDescending: true,
    isAmountDescending: true,
    category: undefined as string | undefined,
    truckId: undefined as string | undefined,
    orderBy: 'transaction_timestamp',
  });

  const [perPage, setPerPage] = useState(20);
  const [loadingDownload] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // UI State
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [showAttachInvoiceModal, setShowAttachInvoiceModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionWithPayments | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Split allocation modal state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitModalTransaction, setSplitModalTransaction] =
    useState<TransactionWithPayments | null>(null);
  const [splitModalTrucks, setSplitModalTrucks] = useState<Truck[]>([]);
  const [isSavingAttributions, setIsSavingAttributions] = useState(false);

  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const allTransactionCategories = [
    ...budgetCategories,
    ...Object.values(transactionDepositCategories),
    ...Object.values(transactionPausedCategories),
  ];

  // Demo Data Logic
  const customerStatus = (userRecord as Customer)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  const shouldFetchRealData = !!userId && !isPreActive;

  const {
    data: transactions = [],
    isLoading,
    isError,
    refetch,
  } = useTransactionsQuery({ userId, ...filters }, shouldFetchRealData);

  const displayTransactions = useMemo(() => {
    return isPreActive
      ? (getDemoTransactions() as TransactionWithPayments[])
      : transactions;
  }, [isPreActive, transactions]);

  const [searchParams, setSearchParams] = useSearchParams();
  const action = searchParams.get('action');
  const transactionId = searchParams.get('transaction_id');

  useEffect(() => {
    if (
      action === 'upload_invoice' &&
      transactionId &&
      !isModalVisible &&
      !showAttachInvoiceModal &&
      !isLoading
    ) {
      const targetTransaction = displayTransactions.find(
        (t) => t.transaction_id === transactionId
      );

      if (targetTransaction) {
        setSelectedTransaction(targetTransaction);
        setShowAttachInvoiceModal(true);
        setSearchParams((prev: URLSearchParams) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('action');
          newParams.delete('transaction_id');
          return newParams;
        });
      }
    }
  }, [
    action,
    transactionId,
    displayTransactions,
    isModalVisible,
    showAttachInvoiceModal,
    setSearchParams,
    isLoading,
  ]);

  const {
    data: transactionsCount = 0,
    isLoading: isLoadingCount,
    isError: isCountError,
    refetch: refetchCount,
  } = useTransactionsCountQuery({ userId, ...filters }, shouldFetchRealData);

  const { updateCategory, updateTruck, updateTruckAttributions } =
    useTransactionMutations(userId, {
      userId,
      ...filters,
    });

  const displayTrucks = useMemo(() => {
    return isPreActive ? getDemoTrucks() : trucks;
  }, [isPreActive, trucks]);

  const displayTransactionsCount = useMemo(() => {
    return isPreActive ? 20 : transactionsCount;
  }, [isPreActive, transactionsCount]);

  const displayLoading = isPreActive ? false : isLoading;
  const displayLoadingCount = isPreActive ? false : isLoadingCount;

  // Handlers
  const handleCategoryChange = async (
    value: string,
    transaction_id: string
  ) => {
    if (isPreActive) {
      toast.error('Complete onboarding to modify transactions');
      return;
    }
    await updateCategory(transaction_id, value);
  };

  const handleTruckChange = async (value: string, transaction_id: string) => {
    if (isPreActive) {
      toast.error('Complete onboarding to modify transactions');
      return;
    }
    await updateTruck(transaction_id, value);
  };

  // Assign a transaction to the business (no truck). An empty attributions
  // array tells the backend to clear attributions and null the truck_id.
  const handleClearTruckAttributions = async (transaction_id: string) => {
    if (isPreActive) {
      toast.error('Complete onboarding to modify transactions');
      return;
    }
    await updateTruckAttributions(transaction_id, []);
  };

  // Handler for opening split allocation modal
  const handleOpenSplitModal = (
    transaction: TransactionWithPayments,
    selectedTrucks: Truck[]
  ) => {
    if (isPreActive) {
      toast.error('Complete onboarding to modify transactions');
      return;
    }
    setSplitModalTransaction(transaction);
    setSplitModalTrucks(selectedTrucks);
    setShowSplitModal(true);
  };

  // Handler for saving truck attributions from split modal
  const handleSaveTruckAttributions = async (
    attributions: TruckAttributionInput[]
  ) => {
    if (!splitModalTransaction) return;

    setIsSavingAttributions(true);
    try {
      await updateTruckAttributions(
        splitModalTransaction.transaction_id,
        attributions
      );
      toast.success('Truck allocations saved successfully');
      setShowSplitModal(false);
      setSplitModalTransaction(null);
      setSplitModalTrucks([]);
    } catch (error) {
      toast.error('Failed to save truck allocations');
      throw error;
    } finally {
      setIsSavingAttributions(false);
    }
  };

  const handleDownload = () => {
    if (isPreActive) {
      toast.error('Complete onboarding to download transactions');
      return;
    }
    setExportModalOpen(true);
  };

  const handleExportSubmit = async (format: 'csv' | 'qbo' | 'ofx') => {
    setIsExporting(true);
    try {
      const response = await callProtectedApi(
        `/transactions/download/${userId ?? ''}`,
        'post',
        {
          searchQuery:
            filters.searchQuery?.length > 0 ? filters.searchQuery : undefined,
          startDate:
            filters.startDate?.length > 0 ? filters.startDate : undefined,
          endDate: filters.endDate?.length > 0 ? filters.endDate : undefined,
          isDateDescending: filters.isDateDescending,
          isAmountDescending: filters.isAmountDescending,
          category: filters.category || undefined,
          truck_id: filters.truckId || undefined,
          format,
        }
      );
      // callProtectedApi resolves null / the error response on failure (it
      // never throws for HTTP errors), so an unchecked call would fall through
      // to the success toast + navigate even on a 500. Status-only check: the
      // job-kickoff endpoint may 2xx with no `data` payload.
      ensureApiSuccess(response, 'Failed to start transactions export');
      setExportModalOpen(false);
      toast.success(
        'Transactions download request is processing. View the status here.'
      );
      navigate('/dashboard/finances/data-exports');
    } catch (error) {
      console.error('Error occurred while downloading transactions: ', error);
      reportApiError(error, 'transactionsExport');
      // A lapsed session isn't fixable by retrying — the app redirects to
      // login — so skip the misleading "try again" toast.
      if (!(error instanceof NotAuthenticatedError)) {
        toast.error('Could not start the export. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters((prev) => ({
      ...prev,
      offset: (page - 1) * pageSize,
      limit: pageSize,
    }));
  };

  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    setFilters((prev) => ({ ...prev, offset: 0, limit: value }));
  };

  const handleSortChange = (value: string) => {
    if (value.includes('transaction_timestamp')) {
      setFilters((prev) => ({
        ...prev,
        isDateDescending: value.includes('desc'),
        orderBy: 'transaction_timestamp',
        offset: 0,
      }));
    } else if (value.includes('amount')) {
      setFilters((prev) => ({
        ...prev,
        isAmountDescending: value.includes('desc'),
        orderBy: 'amount',
        offset: 0,
      }));
    }
  };

  const handleDateChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    if (dates && dates[0] && dates[1]) {
      setFilters((prev) => ({
        ...prev,
        startDate: dates[0]!.format('YYYY-MM-DD'),
        endDate: dates[1]!.format('YYYY-MM-DD'),
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        startDate: '',
        endDate: dayjs().format('YYYY-MM-DD'),
      }));
    }
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      category: '',
      truckId: '',
      orderBy: 'transaction_timestamp',
      offset: 0,
      limit: 20,
    }));
  };

  const togglePopup = (transaction_id: string | undefined) => {
    if (!transaction_id) return;
    setActivePopup(activePopup === transaction_id ? null : transaction_id);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!activePopup) return;
    const clickedPopup = displayTransactions.find(
      (transaction: TransactionWithPayments) => {
        const popupRef = popupRefs.current[transaction?.transaction_id ?? ''];
        return popupRef?.contains(event.target as Node);
      }
    );
    if (!clickedPopup) {
      setActivePopup(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePopup]);

  // Handle Attach Invoice (UI logic)
  const handleAttachInvoice = (transaction: TransactionWithPayments) => {
    if (isPreActive) {
      toast.error('Complete onboarding to attach invoices to transactions');
      setActivePopup(null);
      return;
    }
    setSelectedTransaction(transaction);
    setShowAttachInvoiceModal(true);
    setActivePopup(null);
  };

  const containerClasses = useMemo(() => {
    switch (screenView) {
      case 'desktop':
        return 'flex h-full w-[1148px] max-w-[1148px] flex-col space-y-4 overflow-x-hidden pt-[16px]';
      case 'tablet':
        return 'flex h-full w-full max-w-full flex-col space-y-4 overflow-x-hidden overflow-y-auto px-4 py-[16px]';
      case 'mobile':
        return 'flex h-full w-full max-w-full flex-col space-y-4 overflow-x-hidden overflow-y-auto px-2 py-[16px]';
      default:
        return 'flex h-full w-full flex-col space-y-4';
    }
  }, [screenView]);

  return (
    <>
      {!loadingTrucks ? (
        <div className={containerClasses}>
          {isPreActive && <DemoDataAlert />}
          {/* In-flight deposits + withdrawals (renders nothing until there's one); suppressed for demo customers. */}
          <PendingTransactionsBand enabled={shouldFetchRealData} />
          <TransactionControls
            searchQuery={filters.searchQuery}
            setSearchQuery={(q) =>
              setFilters((p) => ({ ...p, searchQuery: q }))
            }
            startDate={filters.startDate}
            endDate={filters.endDate}
            handleDateChange={handleDateChange}
            disabledDate={(current) =>
              current && current > dayjs().endOf('day')
            }
            handleModalOpen={() => setIsModalVisible(true)}
            handleDownload={handleDownload}
            loadingDownload={loadingDownload}
            setOffset={(val) => setFilters((p) => ({ ...p, offset: val }))}
            isPreActive={isPreActive}
          />

          <TransactionTable
            transactions={displayTransactions}
            isLoading={displayLoading}
            isError={!isPreActive && isError}
            onRetry={() => {
              // Recover both in one click when both failed — the list-error
              // panel is shown instead of the count banner, so its retry must
              // also kick the count.
              refetch();
              if (isCountError) refetchCount();
            }}
            isCountError={!isPreActive && isCountError}
            onRetryCount={() => refetchCount()}
            transactionsCount={displayTransactionsCount}
            isLoadingCount={displayLoadingCount}
            offset={filters.offset}
            limit={filters.limit}
            perPage={perPage}
            togglePopup={togglePopup}
            activePopup={activePopup}
            popupRefs={popupRefs}
            handleAttachInvoice={handleAttachInvoice}
            handlePerPageChange={handlePerPageChange}
            handlePageChange={handlePageChange}
            handleCategoryChange={handleCategoryChange}
            handleTruckChange={handleTruckChange}
            handleOpenSplitModal={handleOpenSplitModal}
            handleClearTruckAttributions={handleClearTruckAttributions}
            budgetCategories={budgetCategories}
            trucks={displayTrucks}
            isPreActive={isPreActive}
          />
        </div>
      ) : (
        <ClipLoader
          color="#1D213B"
          loading={loadingTrucks}
          size={60}
          aria-label="Loading Spinner"
          data-testid="loader"
          className="mt-10 flex h-full w-full items-center justify-center"
        />
      )}

      <FilterModal
        isVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        trucks={trucks}
        allTransactionCategories={allTransactionCategories}
        onTruckChange={(id) =>
          setFilters((p) => ({ ...p, truckId: id || undefined, offset: 0 }))
        }
        onCategoryChange={(cat) =>
          setFilters((p) => ({ ...p, category: cat || undefined, offset: 0 }))
        }
        onSortChange={handleSortChange}
        onClearFilters={handleClearFilters}
        isDateDescending={filters.isDateDescending}
        isAmountDescending={filters.isAmountDescending}
        category={filters.category}
        truckId={filters.truckId}
        orderBy={filters.orderBy}
        isPreActive={isPreActive}
      />
      {showAttachInvoiceModal && !isPreActive && (
        <AttachInvoiceModal
          isOpen={showAttachInvoiceModal}
          onClose={() => setShowAttachInvoiceModal(false)}
          transaction={selectedTransaction}
        />
      )}
      {showSplitModal && splitModalTransaction && !isPreActive && (
        <SplitAllocationModal
          isOpen={showSplitModal}
          onClose={() => {
            setShowSplitModal(false);
            setSplitModalTransaction(null);
            setSplitModalTrucks([]);
          }}
          transaction={splitModalTransaction}
          selectedTrucks={splitModalTrucks}
          onSave={handleSaveTruckAttributions}
          isSaving={isSavingAttributions}
        />
      )}
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onSubmit={handleExportSubmit}
        startDate={filters.startDate}
        endDate={filters.endDate}
        isSubmitting={isExporting}
      />
    </>
  );
}

export default Transactions;
